// KeyFacts — quotable "answer block" (speakable) for AI assistants / featured snippets. Rendered on Home, Rooms, Location.
import React from 'react';
import { MapPin, UtensilsCrossed, BedDouble, Clock, Plane, Users, BadgePercent } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useHotel } from '../context/HotelContext';
import { getKeyFacts, keyFactsVars } from '../i18n/keyFacts';

const ICONS = [MapPin, UtensilsCrossed, BedDouble, Clock, Plane, Users, BadgePercent];
const SITE = (process.env.REACT_APP_SITE_URL || 'https://larosa-negra.com').replace(/\/$/, '');

const KeyFacts = ({ compact = false }) => {
  const { lang } = useLanguage();
  const { hotelInfo, rooms } = useHotel();
  const k = getKeyFacts(lang, keyFactsVars(hotelInfo, rooms));
  const prefix = lang === 'en' ? '' : `/${lang}`;
  const schema = {
    '@context': 'https://schema.org', '@type': 'WebPage', '@id': `${SITE}${prefix}${typeof window !== 'undefined' ? window.location.pathname.replace(new RegExp(`^${prefix}`), '') : '/'}#keyfacts`,
    inLanguage: lang === 'me' ? 'sr-ME' : lang, about: { '@id': `${SITE}/#hotel` },
    speakable: { '@type': 'SpeakableSpecification', cssSelector: ['#key-facts-summary', '#key-facts-list'] },
  };
  const rows = k.facts.map(([label, value], i) => {
    const Icon = ICONS[i] || MapPin;
    const last = i === k.facts.length - 1;
    return (
      <div key={label} className={`flex gap-3 rounded-xl border p-3 sm:p-4 min-w-0 ${last ? 'sm:col-span-2 lg:col-span-3 border-orange-300 bg-orange-50' : 'border-orange-100 bg-white/80'}`} data-testid={`key-fact-${i}`}>
        <div className="w-9 h-9 shrink-0 rounded-lg bg-orange-500/10 text-orange-600 flex items-center justify-center"><Icon size={18} /></div>
        <div className="min-w-0">
          <dt className="text-xs uppercase tracking-wider text-gray-500 font-semibold">{label}</dt>
          <dd className="text-sm text-gray-800 leading-snug break-words">{value}</dd>
        </div>
      </div>
    );
  });
  return (
    <section id="key-facts" aria-labelledby="key-facts-title" className={`${compact ? 'py-10' : 'py-16'} bg-[#fff7ed]`} data-testid="key-facts-section">
      <div className="container mx-auto px-4 max-w-6xl">
        <p className="text-orange-500 font-semibold mb-2 text-sm uppercase tracking-wider" id="key-facts-title">{k.title} · Hotel La Rosa Negra</p>
        <p id="key-facts-summary" className="text-base md:text-lg text-gray-800 max-w-4xl leading-relaxed mb-6" data-testid="key-facts-summary">{k.summary}</p>
        <dl id="key-facts-list" className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">{rows}</dl>
      </div>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />
    </section>
  );
};

export default KeyFacts;
